/**
 * Cross-tab session identity.
 *
 * The browser shares ONE localStorage (and therefore one access token) across
 * every tab, but each tab holds its own `user` in React state — and `user` is
 * where the capability list lives, i.e. the thing the UI shows and hides from.
 * Sign in as somebody else in a second tab and the first tab keeps rendering the
 * PREVIOUS role's buttons while every request it fires already carries the new
 * role's token. The server refuses them, so the affordance was a lie.
 *
 * QA hit exactly that on 2026-08-17: a left-open PM tab offered Add Property /
 * Add Resident / Add Unit / Edit / Delete first to a Maintenance session and
 * then to a Viewer one, each time answered by
 * `Forbidden: requires one of [ORG_ADMIN, PM_MANAGER]`. Every one of those
 * buttons was already correctly gated in the code AND in the deployed bundle —
 * the gate was simply reading a stale identity.
 *
 * So the fix is not another gate. It is making a tab notice that it is no longer
 * who it thought it was.
 */

/** The shape `identityStamp` needs — the session fields that decide capabilities. */
export interface IdentityLike {
  type: string;
  id: string;
  role: string;
  /** Org users only; ops users have no org. */
  orgId?: string;
}

/** localStorage key the stamp is mirrored under, so sibling tabs can watch it. */
export const IDENTITY_KEY = 'authIdentity';

/**
 * WHO a session is, as one comparable string.
 *
 * Role and org are part of it, not just the user id: the same person re-graded
 * from PM to Viewer, or switched into a different org, is a different capability
 * set and must not keep the old tab's buttons.
 */
export function identityStamp(user: IdentityLike | null | undefined): string | null {
  if (!user) return null;
  return `${user.type}:${user.id}:${user.orgId ?? ''}:${user.role}`;
}

/**
 * Should a tab reload in response to a `storage` event?
 *
 * @param changedKey the event's `key` — `null` means `localStorage.clear()`,
 *   which is how a sign-out in another tab arrives.
 * @param storedStamp what `IDENTITY_KEY` holds NOW (after the other tab's write).
 * @param ownStamp the identity THIS tab is currently rendering.
 *
 * Keyed on the identity stamp rather than on `accessToken` deliberately: a
 * routine silent token refresh rewrites `accessToken` on every 401 retry
 * (`lib/api.ts`), and reloading every other tab on each of those would be
 * unusable. Identity changes are rare; token rotation is not.
 */
export function shouldReloadForStorageEvent(
  changedKey: string | null,
  storedStamp: string | null,
  ownStamp: string | null,
): boolean {
  if (changedKey !== null && changedKey !== IDENTITY_KEY) return false;
  return storedStamp !== ownStamp;
}
